import { useQuery } from "@tanstack/react-query";
import { api, fmtNum, timeAgo } from "@/api";
import { ArrowUp, ArrowDown, Lightning } from "@phosphor-icons/react";

// Map every possible entry_status / storage-status combination to a color-coded
// pill so the user can spot at-a-glance whether a trigger is still enterable.
function statusPill(row) {
  // Backend stores { status: 'pending' | 'resolved', outcome: 'win' | 'tp1' | 'loss' | 'timeout' }.
  // Prefer outcome (set after resolution) so we render the correct resolved-pill.
  // Falls back to status when outcome is null (i.e. still pending).
  const stored = row.outcome || row.status;
  const live = row.entry_status; // safe | too_late | tp1_hit | sl_hit | unknown

  if (stored === "win")
    return { label: "TP2 · Full Win", color: "#00FF66", bg: "#00FF6620" };
  if (stored === "tp1")
    return { label: "TP1 · Partial", color: "#8DD35B", bg: "#8DD35B22" };
  if (stored === "loss")
    return { label: "Stopped Out", color: "#FF3B30", bg: "#FF3B3020" };
  if (stored === "timeout")
    return { label: "Timed Out", color: "#666", bg: "#66666622" };
  // Pending — use live status, but tag BREAKEVEN when TP1 already hit and SL moved to entry
  if (row.sl_moved_be)
    return { label: "TP1 HIT · BE LOCKED", color: "#00FF66", bg: "#00FF6622" };
  if (live === "safe")
    return { label: "SAFE TO ENTER", color: "#00FF66", bg: "#00FF6622" };
  if (live === "too_late")
    return { label: "TOO LATE", color: "#F5C518", bg: "#F5C51822" };
  if (live === "tp1_hit")
    return { label: "TP1 hit · late", color: "#8DD35B", bg: "#8DD35B22" };
  if (live === "sl_hit")
    return { label: "SL hit · dead", color: "#FF3B30", bg: "#FF3B3020" };
  return { label: "Loading…", color: "#666", bg: "#66666622" };
}

function rowTint(row) {
  const pill = statusPill(row);
  // Very subtle left-border accent so the row remains legible.
  return { borderLeft: `2px solid ${pill.color}` };
}

export function TriggerHistory({ onSelect }) {
  const q = useQuery({
    queryKey: ["trigger-history"],
    queryFn: () => api.triggerHistory(50),
    refetchInterval: 15_000,
  });

  const rows = q.data?.triggers || [];
  const priceAge = q.data?.price_cache_updated_at
    ? timeAgo(q.data.price_cache_updated_at)
    : "—";

  // Bucket counts
  const c = { safe: 0, too_late: 0, tp1_hit: 0, sl_hit: 0, resolved: 0 };
  for (const r of rows) {
    if (r.status === "pending") {
      c[r.entry_status] = (c[r.entry_status] || 0) + 1;
    } else {
      c.resolved += 1;
    }
  }

  return (
    <div
      className="border border-white/10 bg-[#0d0d0d]"
      data-testid="trigger-history"
    >
      <div className="px-3 py-2 border-b border-white/10 flex items-center gap-3 flex-wrap">
        <Lightning size={14} className="text-[#00FF66]" weight="fill" />
        <span className="font-display text-sm tracking-widest uppercase text-white">
          Trigger History
        </span>
        <span className="font-mono text-[10px] text-white/40">
          Every fired signal · live enterability
        </span>
        <span className="ml-auto font-mono text-[10px] text-white/40 flex gap-3 flex-wrap">
          <span data-testid="trig-count-safe">
            <span className="text-[#00FF66]">●</span> {c.safe || 0} safe
          </span>
          <span data-testid="trig-count-late">
            <span className="text-[#F5C518]">●</span> {c.too_late || 0} late
          </span>
          <span>
            <span className="text-[#8DD35B]">●</span> {c.tp1_hit || 0} tp1
          </span>
          <span>
            <span className="text-[#FF3B30]">●</span> {c.sl_hit || 0} stopped
          </span>
          <span data-testid="trig-count-resolved">
            <span className="text-white/40">●</span> {c.resolved} resolved
          </span>
          <span className="text-white/30">price {priceAge}</span>
        </span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-[11px] font-mono">
          <thead className="text-white/40 uppercase tracking-widest">
            <tr className="border-b border-white/10">
              <th className="text-left px-3 py-2">Symbol</th>
              <th className="text-left px-2 py-2">Dir</th>
              <th className="text-left px-2 py-2">Mode</th>
              <th className="text-right px-2 py-2">Score</th>
              <th className="text-right px-2 py-2">Entry</th>
              <th className="text-right px-2 py-2">Current</th>
              <th className="text-right px-2 py-2">Dist (R)</th>
              <th className="text-left px-2 py-2">Status</th>
              <th className="text-right px-3 py-2">Fired</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td
                  colSpan={9}
                  className="px-3 py-6 text-center text-white/40"
                >
                  {q.isLoading
                    ? "Loading trigger history…"
                    : "No triggers fired yet. First one will appear here the moment scanners catch a setup."}
                </td>
              </tr>
            )}
            {rows.map((r) => {
              const pill = statusPill(r);
              const isLong = r.direction === "long";
              const distColor =
                r.entry_distance_r == null
                  ? "#666"
                  : r.entry_distance_r > 0.5
                    ? "#F5C518"
                    : r.entry_distance_r < -0.3
                      ? "#8DD35B"
                      : "#fff";
              return (
                <tr
                  key={r.id}
                  data-testid={`trigger-row-${r.symbol}-${r.direction}-${r.mode}`}
                  onClick={() =>
                    onSelect && onSelect(r.symbol, r.direction, r.mode)
                  }
                  className="border-b border-white/5 hover:bg-white/[0.03] cursor-pointer transition-colors"
                  style={rowTint(r)}
                >
                  <td className="px-3 py-1.5 text-white font-bold">
                    {r.symbol}
                  </td>
                  <td className="px-2 py-1.5">
                    <span
                      className="inline-flex items-center gap-0.5"
                      style={{ color: isLong ? "#00FF66" : "#FF3B30" }}
                    >
                      {isLong ? (
                        <ArrowUp size={10} weight="bold" />
                      ) : (
                        <ArrowDown size={10} weight="bold" />
                      )}
                      {r.direction?.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-2 py-1.5 text-white/60 uppercase">
                    {r.mode}
                  </td>
                  <td className="px-2 py-1.5 text-right text-white">
                    {r.score ? Math.round(r.score) : "—"}
                  </td>
                  <td className="px-2 py-1.5 text-right text-white/70">
                    {fmtNum(r.entry)}
                  </td>
                  <td className="px-2 py-1.5 text-right text-white/90">
                    {r.current_price ? fmtNum(r.current_price) : "—"}
                  </td>
                  <td
                    className="px-2 py-1.5 text-right font-bold"
                    style={{ color: distColor }}
                  >
                    {r.entry_distance_r == null
                      ? "—"
                      : (r.entry_distance_r > 0 ? "+" : "") +
                        r.entry_distance_r.toFixed(2)}
                  </td>
                  <td className="px-2 py-1.5">
                    <span
                      className="inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest"
                      style={{
                        color: pill.color,
                        background: pill.bg,
                        border: `1px solid ${pill.color}66`,
                      }}
                      data-testid={`trigger-status-${r.symbol}`}
                    >
                      {pill.label}
                    </span>
                  </td>
                  <td className="px-3 py-1.5 text-right text-white/50">
                    {r.created_at ? timeAgo(r.created_at) : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="px-3 py-1.5 border-t border-white/10 flex items-center gap-3 text-[10px] font-mono text-white/40 flex-wrap">
        <span>Dist (R):</span>
        <span>
          <span style={{ color: "#8DD35B" }}>−0.3</span> price moved back below
          entry — better than intended entry (still safe)
        </span>
        <span>
          <span className="text-white">0</span> at entry
        </span>
        <span>
          <span style={{ color: "#F5C518" }}>+0.5</span> past sweet zone → too
          late
        </span>
        <span className="ml-auto">Auto-refresh every 15s</span>
      </div>
    </div>
  );
}
